import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const FinancialReports = ({ weeklyStats, monthlyStats, analytics }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedReport, setSelectedReport] = useState('revenue');

  const revenueData = analytics?.revenueData?.map(item => ({
    ...item,
    fill: '#10b981'
  }));

  const commissionData = [
    { name: 'Sua Receita', value: 85, amount: 61072.71, color: '#10b981' },
    { name: 'Comissão Plataforma', value: 15, amount: 10777.54, color: '#6366f1' }
  ];

  const paymentMethodData = [
    { method: 'PIX', amount: 32540.50, percentage: 45.3 },
    { method: 'Cartão Débito', amount: 21693.75, percentage: 30.2 },
    { method: 'Cartão Crédito', amount: 12456.80, percentage: 17.4 },
    { method: 'Dinheiro', value: 5159.20, percentage: 7.1 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-3">
          <p className="text-sm font-medium text-card-foreground mb-1">
            {label}
          </p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`Receita: R$ ${entry?.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (selectedReport) {
      case 'revenue':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(value) => `R$ ${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="revenue"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'commission':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={commissionData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                paddingAngle={5}
                dataKey="value"
              >
                {commissionData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `R$ ${props?.payload?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  name
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Relatórios Financeiros
          </h2>
          <p className="text-muted-foreground">
            Análise detalhada de receitas e comissões da plataforma
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Select
            value={selectedPeriod}
            onChange={(value) => setSelectedPeriod(value)}
            className="w-32"
          >
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
            <option value="yearly">Anual</option>
          </Select>

          <Select
            value={selectedReport}
            onChange={(value) => setSelectedReport(value)}
            className="w-40"
          >
            <option value="revenue">Receita</option>
            <option value="commission">Comissões</option>
            <option value="payments">Pagamentos</option>
          </Select>

          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            iconSize={16}
          >
            Exportar
          </Button>
        </div>
      </div>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Receita Bruta</p>
              <p className="text-2xl font-heading font-bold text-card-foreground">
                R$ {monthlyStats?.totalRevenue?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-success">+12.5% vs mês anterior</p>
            </div>
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="TrendingUp" size={20} className="text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Comissão Plataforma</p>
              <p className="text-2xl font-heading font-bold text-card-foreground">
                R$ {(monthlyStats?.totalRevenue * 0.15)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground">15% da receita bruta</p>
            </div>
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Percent" size={20} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Receita Líquida</p>
              <p className="text-2xl font-heading font-bold text-card-foreground">
                R$ {(monthlyStats?.totalRevenue * 0.85)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-success">Após comissões</p>
            </div>
            <div className="p-2 bg-warning/10 rounded-lg">
              <Icon name="DollarSign" size={20} className="text-warning" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Ticket Médio</p>
              <p className="text-2xl font-heading font-bold text-card-foreground">
                R$ {weeklyStats?.averageBookingValue?.toFixed(2)}
              </p>
              <p className="text-xs text-info">Por reserva</p>
            </div>
            <div className="p-2 bg-info/10 rounded-lg">
              <Icon name="Calculator" size={20} className="text-info" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Chart */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-bold text-card-foreground">
              {selectedReport === 'revenue' ? 'Evolução da Receita' : 'Distribuição de Comissões'}
            </h3>
          </div>
          {renderChart()}
        </div>

        {/* Payment Methods */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
          <h3 className="text-lg font-heading font-bold text-card-foreground mb-4">
            Métodos de Pagamento
          </h3>
          <div className="space-y-4">
            {paymentMethodData?.map((method, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon 
                      name={method?.method === 'PIX' ? 'Smartphone' : 'CreditCard'} 
                      size={16} 
                      className="text-primary" 
                    />
                  </div>
                  <div>
                    <div className="font-medium text-card-foreground">
                      {method?.method}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {method?.percentage}% do total
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-card-foreground">
                    R$ {method?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Banking Integration */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-bold text-card-foreground">
              Integração Bancária
            </h3>
            <Button variant="outline" size="sm">
              Configurar
            </Button>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-success/20 rounded-lg">
                  <Icon name="Building2" size={16} className="text-success" />
                </div>
                <div>
                  <div className="font-medium text-success">Banco do Brasil</div>
                  <div className="text-sm text-muted-foreground">Conta corrente integrada</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-lg font-heading font-bold text-card-foreground">
                  98%
                </div>
                <div className="text-xs text-muted-foreground">Conciliação automática</div>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded-lg">
                <div className="text-lg font-heading font-bold text-card-foreground">
                  2-3h
                </div>
                <div className="text-xs text-muted-foreground">Tempo de liquidação</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Information */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
          <h3 className="text-lg font-heading font-bold text-card-foreground mb-4">
            Informações Tributárias
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <div className="font-medium text-card-foreground">CNPJ</div>
                <div className="text-sm text-muted-foreground">12.345.678/0001-90</div>
              </div>
              <Icon name="FileText" size={16} className="text-muted-foreground" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">ISS (3%)</label>
                <div className="text-lg font-semibold text-card-foreground">
                  R$ {(monthlyStats?.totalRevenue * 0.03)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Simples Nacional</label>
                <div className="text-lg font-semibold text-card-foreground">
                  R$ {(monthlyStats?.totalRevenue * 0.06)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <Button variant="outline" size="sm" className="w-full">
              Gerar Relatório Fiscal
            </Button>
          </div>
        </div>
      </div>

      {/* Reconciliation Status */}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
        <h3 className="text-lg font-heading font-bold text-card-foreground mb-4">
          Status da Reconciliação
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center justify-center w-12 h-12 bg-success/20 rounded-lg mx-auto mb-3">
              <Icon name="CheckCircle" size={24} className="text-success" />
            </div>
            <div className="text-lg font-heading font-bold text-success">
              R$ {(monthlyStats?.totalRevenue * 0.92)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-muted-foreground">Pagamentos conciliados</div>
          </div>

          <div className="text-center p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center justify-center w-12 h-12 bg-warning/20 rounded-lg mx-auto mb-3">
              <Icon name="Clock" size={24} className="text-warning" />
            </div>
            <div className="text-lg font-heading font-bold text-warning">
              R$ {(monthlyStats?.totalRevenue * 0.06)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-muted-foreground">Pendente de liquidação</div>
          </div>

          <div className="text-center p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center justify-center w-12 h-12 bg-destructive/20 rounded-lg mx-auto mb-3">
              <Icon name="AlertCircle" size={24} className="text-destructive" />
            </div>
            <div className="text-lg font-heading font-bold text-destructive">
              R$ {(monthlyStats?.totalRevenue * 0.02)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-muted-foreground">Requer atenção</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReports;